import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AuthKind } from "../lib/api";
import { useToast } from "./Toast";
import RelativeTime from "./RelativeTime";

interface AuthSpec {
  kind: AuthKind;
  fields: Record<string, string>;
}

interface Props {
  kind: AuthKind;
  fields: Record<string, string>;
  onChange: (next: AuthSpec) => void;
  // PKCE sign-in needs the saved server-side config — pass the request
  // id and whether the auth tab has unsaved edits.
  requestId?: string;
  dirty?: boolean;
}

// Per-request auth editor. Lives in the new "Auth" tab inside
// RequestBuilder. The shape mirrors the server's auth-apply contract:
// every form is a flat key/value map under `fields`.
export default function AuthEditor({ kind, fields, onChange, requestId, dirty }: Props) {
  const set = (patch: Record<string, string>) => {
    onChange({ kind, fields: { ...fields, ...patch } });
  };
  const setKind = (next: AuthKind) => {
    // Switching to a new auth kind discards stale fields so a half-typed
    // OAuth config doesn't haunt a later Bearer save. We DO keep `none`
    // as the empty state and clear everything else when leaving it.
    onChange({ kind: next, fields: {} });
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="space-y-2">
        <label className="label">Authentication kind</label>
        <select
          className="input mono w-72"
          value={kind}
          onChange={(e) => setKind(e.target.value as AuthKind)}
        >
          <option value="none">None — send no credentials</option>
          <option value="basic">Basic — username + password</option>
          <option value="bearer">Bearer — opaque token</option>
          <option value="oauth2">OAuth 2.0 — token exchange + refresh</option>
          <option value="aws-sigv4">AWS Signature v4</option>
          <option value="digest">HTTP Digest (challenge/response)</option>
        </select>
        <Description kind={kind} />
      </div>

      {kind === "basic" && (
        <Section title="Basic credentials">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
        </Section>
      )}

      {kind === "bearer" && (
        <Section title="Bearer token">
          <Field label="Token" value={fields.token ?? ""} onChange={(v) => set({ token: v })} type="password" mono />
          <p className="text-xs muted">Sent as <code className="mono">Authorization: Bearer &lt;token&gt;</code>. Use env vars (<code className="mono">{"{{API_TOKEN}}"}</code>) if you don't want the literal value here.</p>
        </Section>
      )}

      {kind === "oauth2" && <OAuth2Fields fields={fields} set={set} requestId={requestId} dirty={dirty} />}

      {kind === "aws-sigv4" && (
        <Section title="AWS credentials">
          <Field label="Access Key ID" value={fields.access_key ?? ""} onChange={(v) => set({ access_key: v })} mono />
          <Field label="Secret Access Key" value={fields.secret_key ?? ""} onChange={(v) => set({ secret_key: v })} type="password" mono />
          <Field label="Session Token (optional)" value={fields.session_token ?? ""} onChange={(v) => set({ session_token: v })} type="password" mono />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Region" value={fields.region ?? ""} onChange={(v) => set({ region: v })} placeholder="us-east-1" mono />
            <Field label="Service" value={fields.service ?? ""} onChange={(v) => set({ service: v })} placeholder="s3 / execute-api / ec2 / …" mono />
          </div>
          <p className="text-xs muted">Signs every outbound request with SigV4. <code className="mono">x-amz-content-sha256</code> is only added for S3 — other services follow the AWS SDK convention.</p>
        </Section>
      )}

      {kind === "digest" && (
        <Section title="HTTP Digest credentials">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
          <div className="space-y-1">
            <label className="label">Algorithm</label>
            <select
              className="input mono w-48"
              value={fields.algorithm ?? "MD5"}
              onChange={(e) => set({ algorithm: e.target.value })}
            >
              <option value="MD5">MD5 (legacy)</option>
              <option value="SHA-256">SHA-256</option>
            </select>
          </div>
          <p className="text-xs muted">First fetch goes out unauthenticated; on the server's 401 challenge we compute the digest reply and re-send automatically.</p>
        </Section>
      )}
    </div>
  );
}

function OAuth2Fields({ fields, set, requestId, dirty }: {
  fields: Record<string, string>;
  set: (p: Record<string, string>) => void;
  requestId?: string;
  dirty?: boolean;
}) {
  const grant = fields.grant ?? "client_credentials";
  return (
    <Section title="OAuth 2.0 configuration">
      <div className="space-y-1">
        <label className="label">Grant type</label>
        <select
          className="input mono w-72"
          value={grant}
          onChange={(e) => set({ grant: e.target.value })}
        >
          <option value="client_credentials">client_credentials — machine-to-machine</option>
          <option value="authorization_code">authorization_code (with PKCE) — user sign-in</option>
          <option value="password">password — username + password</option>
        </select>
      </div>

      <Field label="Token URL" value={fields.token_url ?? ""} onChange={(v) => set({ token_url: v })} placeholder="https://idp.example.com/oauth/token" mono />
      {grant === "authorization_code" && (
        <Field label="Authorization URL" value={fields.auth_url ?? ""} onChange={(v) => set({ auth_url: v })} placeholder="https://idp.example.com/oauth/authorize" mono />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Client ID" value={fields.client_id ?? ""} onChange={(v) => set({ client_id: v })} mono />
        <Field
          label={grant === "authorization_code" ? "Client Secret (optional with PKCE)" : "Client Secret"}
          value={fields.client_secret ?? ""}
          onChange={(v) => set({ client_secret: v })}
          type="password"
          mono
        />
      </div>

      {grant === "password" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
        </div>
      )}

      {grant === "authorization_code" && (
        <Field label="Redirect URI" value={fields.redirect_uri ?? ""} onChange={(v) => set({ redirect_uri: v })} placeholder={`${window.location.origin}/oauth/callback`} mono />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Scope (space-separated)" value={fields.scope ?? ""} onChange={(v) => set({ scope: v })} placeholder="read write" mono />
        <Field label="Audience (Auth0 / OIDC)" value={fields.audience ?? ""} onChange={(v) => set({ audience: v })} mono />
      </div>

      {grant === "authorization_code" && requestId && (
        <PkceSignIn fields={fields} requestId={requestId} dirty={dirty} />
      )}

      <p className="text-xs muted">
        We exchange these for an access token on first send and cache it (encrypted at rest) keyed by user. Tokens auto-refresh from <code className="mono">refresh_token</code> when present; otherwise we re-run the grant. Changing any field above invalidates the cache.
      </p>
    </Section>
  );
}

// "Sign in" button for the authorization-code grant. Opens the IdP in a
// popup with a PKCE challenge; the /oauth/callback page relays the code
// back via postMessage and we POST it to /api/oauth/exchange, which
// stores the token server-side under the same fingerprint applyAuth()
// uses at run time.
function PkceSignIn({ fields, requestId, dirty }: { fields: Record<string, string>; requestId: string; dirty?: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pendingState, setPendingState] = useState<string | null>(null);
  const statusQ = useQuery({
    queryKey: ["oauth-status", requestId],
    queryFn: () => api.oauth.status(requestId),
  });
  const exchange = useMutation({
    mutationFn: (vars: { code: string; code_verifier: string }) =>
      api.oauth.exchange({ request_id: requestId, ...vars }),
    onSuccess: () => {
      toast.success("Signed in — token stored");
      qc.invalidateQueries({ queryKey: ["oauth-status", requestId] });
    },
    onError: (e) => toast.error(`Token exchange failed: ${(e as Error).message}`),
    onSettled: () => setPendingState(null),
  });

  useEffect(() => {
    if (!pendingState) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; code?: string; state?: string };
      if (d?.type !== "apistash:oauth_code" || d.state !== pendingState || !d.code) return;
      const raw = sessionStorage.getItem(`pkce:${pendingState}`);
      sessionStorage.removeItem(`pkce:${pendingState}`);
      if (!raw) return;
      const { verifier } = JSON.parse(raw) as { verifier: string };
      exchange.mutate({ code: d.code, code_verifier: verifier });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [pendingState]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSignIn = !!fields.auth_url && !!fields.token_url && !!fields.client_id && !dirty;

  const startSignIn = async () => {
    const verifier = randomToken(64);
    const challenge = await pkceChallenge(verifier);
    const state = randomToken(24);
    sessionStorage.setItem(`pkce:${state}`, JSON.stringify({ verifier }));
    setPendingState(state);
    let u: URL;
    try { u = new URL(fields.auth_url); } catch { toast.error("Authorization URL is not a valid URL"); return; }
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", fields.client_id ?? "");
    u.searchParams.set("redirect_uri", fields.redirect_uri || `${window.location.origin}/oauth/callback`);
    if (fields.scope) u.searchParams.set("scope", fields.scope);
    if (fields.audience) u.searchParams.set("audience", fields.audience);
    u.searchParams.set("state", state);
    u.searchParams.set("code_challenge", challenge);
    u.searchParams.set("code_challenge_method", "S256");
    window.open(u.toString(), "apistash_oauth", "popup,width=540,height=720");
  };

  const s = statusQ.data;
  return (
    <div className="rounded border border-border bg-surface-2 p-3 flex flex-wrap items-center gap-3">
      <button
        type="button"
        className="btn-primary text-sm"
        disabled={!canSignIn || exchange.isPending || !!pendingState}
        onClick={startSignIn}
        title={dirty ? "Save the request first — the sign-in uses the saved config" : undefined}
      >
        {exchange.isPending ? "Exchanging…" : pendingState ? "Waiting for sign-in…" : s?.connected ? "Sign in again" : "Sign in"}
      </button>
      <div className="text-xs muted-strong">
        {s?.connected ? (
          <>
            <span className="text-good">● Signed in</span>
            {s.expires_at && <> · expires <RelativeTime iso={s.expires_at} /></>}
            {s.has_refresh && <> · auto-refreshes</>}
          </>
        ) : dirty ? (
          <span>Save the request, then sign in.</span>
        ) : !canSignIn ? (
          <span>Fill in Authorization URL, Token URL and Client ID to enable sign-in.</span>
        ) : (
          <span>Not signed in yet.</span>
        )}
      </div>
    </div>
  );
}

function randomToken(len: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, mono = false }: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: "text" | "password";
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="label">{label}</label>
      <input
        className={"input w-full " + (mono ? "mono" : "")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}

function Description({ kind }: { kind: AuthKind }) {
  const msg = (() => {
    switch (kind) {
      case "none":      return "Send the request as-is.";
      case "basic":     return "Sends Authorization: Basic <base64(username:password)>.";
      case "bearer":    return "Sends Authorization: Bearer <token>.";
      case "oauth2":    return "Token exchange against an IdP, cached + refreshed per user. Supports machine-to-machine, user sign-in, and password grants.";
      case "aws-sigv4": return "Signs the request with AWS Signature v4 — works for any AWS API that accepts SigV4.";
      case "digest":    return "Two-leg HTTP Digest challenge/response (RFC 2617). First request goes out unauthenticated; the server's 401 nonce is used to compute the reply.";
      default:          return "";
    }
  })();
  if (!msg) return null;
  return <p className="text-xs muted">{msg}</p>;
}
